import React from 'react';
import './SobrePage.css';

const OBJECTIVES = [
  {
    icon: '🗺️',
    title: 'Visualização Geoespacial',
    text: 'Mapas interativos com camadas WMS que permitem análise territorial do saneamento básico em cada município.',
  },
  {
    icon: '📊',
    title: 'Monitoramento do PMSB',
    text: 'Acompanhe os horizontes de planejamento do Plano Municipal de Saneamento Básico, de 2024 até 2046.',
  },
  {
    icon: '🤝',
    title: 'Participação Social',
    text: 'Plataforma aberta à sociedade, gestores, prestadoras e reguladores, com diferentes perfis de acesso.',
  },
  {
    icon: '♿',
    title: 'Acessibilidade',
    text: 'Conformidade com a LBI 13.146/2015, E-MAG e WCAG 2.1 AA. VLibras integrado para usuários de Libras.',
  },
  {
    icon: '🔒',
    title: 'Privacidade (LGPD)',
    text: 'Tratamento de dados em conformidade com a Lei 13.709/2018. Coleta mínima e finalidade clara.',
  },
  {
    icon: '📡',
    title: 'Dados Abertos',
    text: 'Camadas públicas disponíveis via WMS/WFS, integradas com infraestrutura GeoServer e PostGIS.',
  },
];

const PERIODS = [
  {
    years: '2024–2026', label: 'Situacional', color: '#0d7c8a',
    subtitle: 'Etapa de diagnóstico e estruturação do plano.',
    detail: 'Aqui são levantadas as condições atuais dos serviços, identificadas deficiências e analisados dados técnicos, operacionais e sociais. Essa fase fundamenta todas as decisões futuras do planejamento.',
  },
  {
    years: '2027–2029', label: 'Imediata', color: '#c8960a',
    subtitle: 'Início das ações prioritárias.',
    detail: 'Contempla intervenções urgentes e de maior impacto, voltadas à correção de problemas críticos identificados no diagnóstico. Foco em resultados rápidos e na estabilização dos serviços.',
  },
  {
    years: '2030–2034', label: 'Curto Prazo', color: '#c86020',
    subtitle: 'Consolidação das melhorias iniciais.',
    detail: 'Engloba ações estruturantes que ampliam a eficiência dos serviços e dão continuidade às soluções iniciadas na fase imediata. Aqui começa a construção de um cenário mais estável e organizado.',
  },
  {
    years: '2035–2038', label: 'Médio Prazo', color: '#8b3a30',
    subtitle: 'Expansão e qualificação dos serviços.',
    detail: 'Nesta fase, os investimentos são voltados à ampliação da cobertura, modernização de sistemas e melhoria da qualidade operacional. O foco está no crescimento sustentável e na otimização dos processos.',
  },
  {
    years: '2039–2046', label: 'Longo Prazo', color: '#5c6e25',
    subtitle: 'Visão de futuro e sustentabilidade plena.',
    detail: 'Abrange ações de longo alcance, com foco em inovação, resiliência e manutenção dos padrões de qualidade alcançados. Representa a consolidação definitiva das metas do plano.',
  },
];

export default function SobrePage({ onNavigate }) {
  return (
    <main className="sobre-page">

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="sobre-hero" aria-label="Apresentação do projeto">
        <div className="sobre-hero__content">
          <button
            className="sobre-back-btn"
            onClick={() => onNavigate('home')}
            aria-label="Voltar ao mapa principal"
          >
            ← Voltar ao Mapa
          </button>
          <span className="sobre-eyebrow">Sobre o Projeto</span>
          <h1 className="sobre-title">Painel InfoVivo</h1>
          <p className="sobre-lead">
            Uma plataforma de transparência, análise e monitoramento do saneamento básico
            nos municípios brasileiros participantes do Plano Municipal de Saneamento Básico.
          </p>
          <div className="sobre-hero-actions">
            <button className="sobre-btn sobre-btn--primary" onClick={() => onNavigate('home')}>
              Explorar o Mapa
            </button>
            <button className="sobre-btn sobre-btn--ghost" onClick={() => onNavigate('tutorial')}>
              Ver Tutorial
            </button>
          </div>
        </div>
        <div className="sobre-hero__mascote-wrap" aria-hidden="true">
          <img
            src="/mascote.png"
            alt=""
            className="sobre-mascote"
          />
          <div className="sobre-mascote-bubble">
            Conheça nosso projeto!
          </div>
        </div>
      </section>

      <div className="sobre-container">

        {/* ── Missão ────────────────────────────────────── */}
        <section className="sobre-section" aria-labelledby="sobre-missao-title">
          <h2 id="sobre-missao-title" className="sobre-section-title">Nossa Missão</h2>
          <p className="sobre-section-text">
            O <strong>Painel InfoVivo</strong> foi desenvolvido para apoiar a gestão pública do saneamento básico,
            oferecendo uma interface moderna e acessível para visualização de dados geoespaciais do PMSB.
            Buscamos aproximar cidadãos, gestores, prestadoras de serviço e órgãos reguladores por meio da informação.
          </p>
          <p className="sobre-section-text">
            Os dados integram os horizontes de planejamento do PMSB, desde o diagnóstico situacional (2024–2026)
            até o horizonte de longo prazo (2039–2046), permitindo o acompanhamento contínuo das metas de
            universalização dos serviços de água, esgoto, resíduos sólidos e drenagem urbana.
          </p>
        </section>

        {/* ── Objetivos ─────────────────────────────────── */}
        <section className="sobre-section" aria-labelledby="sobre-obj-title">
          <h2 id="sobre-obj-title" className="sobre-section-title">O que oferecemos</h2>
          <div className="sobre-obj-grid">
            {OBJECTIVES.map((obj) => (
              <article key={obj.title} className="sobre-obj-card">
                <span className="sobre-obj-icon" aria-hidden="true">{obj.icon}</span>
                <h3 className="sobre-obj-title">{obj.title}</h3>
                <p className="sobre-obj-text">{obj.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ── Horizontes PMSB ───────────────────────────── */}
        <section className="sobre-section" aria-labelledby="sobre-pmsb-title">
          <h2 id="sobre-pmsb-title" className="sobre-section-title">Horizontes do PMSB</h2>
          <p className="sobre-section-text">
            O Plano Municipal de Saneamento Básico é estruturado em cinco horizontes de planejamento:
          </p>
          <div className="sobre-periods-grid">
            {PERIODS.map((p, i) => (
              <div key={p.label} className="sobre-period-card" style={{ '--period-color': p.color }}>
                <div className="sobre-period-card__header">
                  <span className="sobre-period-card__num">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <span className="sobre-period-card__label">{p.label}</span>
                    <span className="sobre-period-card__years">{p.years}</span>
                  </div>
                </div>
                <p className="sobre-period-card__subtitle">{p.subtitle}</p>
                <p className="sobre-period-card__detail">{p.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Contato rápido ────────────────────────────── */}
        <section className="sobre-section sobre-section--cta" aria-labelledby="sobre-cta-title">
          <h2 id="sobre-cta-title" className="sobre-section-title">Entre em Contato</h2>
          <p className="sobre-section-text">
            Tem dúvidas, sugestões ou quer saber mais sobre o projeto?
          </p>
          <button
            className="sobre-btn sobre-btn--primary"
            onClick={() => onNavigate('fale-conosco')}
          >
            Fale Conosco
          </button>
        </section>

      </div>
    </main>
  );
}
